import React from 'react';
import Tooltip from './common/Tooltip';
import { format } from 'date-fns';
import { formatPrice, formatCurrency, formatAmount, parseFloatFromString, getChangeColorClass } from '../utils/formatters';
import { TransactionType } from '../utils/enums';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import FailedTransactionWarning from './FailedTransactionWarning';

// Unrealized PnL of a BUY/TRANSFER_IN lot. When the server provides the FIFO remainder
// (remainingQuantity/remainingCostBasis, portfolio currency), only the unsold part counts.
// Otherwise the whole lot is treated as unsold.
const computeUnrealizedPnL = (transaction, txType, currentPrice) => {
  if (transaction.accountingWarning) return null;
  if ((txType !== TransactionType.BUY && txType !== TransactionType.TRANSFER_IN) || !currentPrice || !transaction.quantity) {
    return null;
  }
  const price = parseFloatFromString(currentPrice);
  const quantity = parseFloatFromString(transaction.quantity);
  if (!(quantity > 0)) return null;

  if (transaction.remainingQuantity != null && transaction.remainingCostBasis != null) {
    const remainingQty = parseFloatFromString(transaction.remainingQuantity);
    if (!(remainingQty > 0)) {
      return { pnl: null, sold: true, tip: 'Fully sold (FIFO)' };
    }
    const pnl = remainingQty * price - parseFloatFromString(transaction.remainingCostBasis);
    const tip = remainingQty < quantity
      ? `On ${formatAmount(transaction.remainingQuantity, '')} of ${formatAmount(transaction.quantity, '')} still held (FIFO)`
      : null;
    return { pnl, tip };
  }

  const sv = transaction?.scaledValues;
  const cost = sv?.cost != null
    ? parseFloatFromString(sv.cost)
    : (parseFloatFromString(transaction.cost) || (parseFloatFromString(transaction.price) * quantity));
  return { pnl: quantity * price - cost, tip: null };
};

const TransactionRow = ({ transaction, token, currentPrice, tokenLocations = [], onEdit, onDelete, onToggleEnabled, onMove, isSelected, onToggleSelect }) => {
  const { currentPortfolio } = usePortfolioContext();
  // Normalize transaction type to always use uppercase enum format
  const txType = transaction.transactionType?.toUpperCase?.() || transaction.transactionType;
  const sv = transaction?.scaledValues;
  const unrealized = computeUnrealizedPnL(transaction, txType, currentPrice);
  const unrealizedPnL = unrealized?.pnl ?? null;

  // Realized PnL for sells and transfers out (transfer out realizes -cost basis); portfolio currency
  const realizedPnL = (txType === TransactionType.SELL || txType === TransactionType.TRANSFER_OUT) && transaction.realizedPnl
    ? formatCurrency(transaction.realizedPnl, currentPortfolio?.currency || 'USD') 
    : "-";

  // Format the date
  const formattedDate = format(new Date(transaction.timestamp), 'dd MMM yyyy');
  const formattedTime = format(new Date(transaction.timestamp), 'HH:mm');
  
  // Get display name for the transaction type
  const txTypeDisplay = TransactionType.getDisplayName(txType);
  
  // Get color class for the transaction type
  const txTypeColor = TransactionType.getTypeColor(txType);

  // Determine quantity color based on transaction type
  const getQuantityColor = () => {
    if (txType === TransactionType.BUY || txType === TransactionType.TRANSFER_IN) {
      return 'text-green-600 dark:text-green-500';
    } else if (txType === TransactionType.SELL || txType === TransactionType.TRANSFER_OUT) {
      return 'text-red-600 dark:text-red-500';
    }
    return '';
  };

  // Format quantity with appropriate sign based on transaction type
  const formatQuantity = () => {
    if (txType === TransactionType.SPLIT) return `×${transaction.quantity}`;
    const isOut = txType === TransactionType.SELL || txType === TransactionType.TRANSFER_OUT;
    return isOut ? `-${transaction.quantity}` : `+${transaction.quantity}`;
  };

  // Tooltip helpers for scaled values
  const portfolioCurrency = currentPortfolio?.currency || 'USD';
  const showTip = !!sv; // show tooltip whenever server provided scaled values
  const priceTip = showTip && sv?.price ? `In ${portfolioCurrency}: ${formatPrice(sv.price, portfolioCurrency)}` : null;
  const costTip = showTip && sv?.cost && txType === TransactionType.BUY ? `In ${portfolioCurrency}: ${formatCurrency(sv.cost, portfolioCurrency)}` : null;
  const proceedsTip = showTip && sv?.proceeds && (txType === TransactionType.SELL || txType === TransactionType.TRANSFER_OUT) ? `In ${portfolioCurrency}: ${formatCurrency(sv.proceeds, portfolioCurrency)}` : null;
  const feesTip = showTip && sv?.fees && transaction.fees !== '0' ? `In ${portfolioCurrency}: ${formatCurrency(sv.fees, portfolioCurrency)}` : null;
  const hasPositiveDecimal = (value) => parseFloatFromString(value) > 0;
  const currencyFee = hasPositiveDecimal(transaction.fees) ? formatCurrency(transaction.fees, transaction.currency) : null;
  const tokenFeeFull = hasPositiveDecimal(transaction.feeQuantity) ? String(transaction.feeQuantity) : null;
  const tokenFeeShort = tokenFeeFull ? formatAmount(tokenFeeFull, '') : null;
  const tokenTicker = token?.ticker || transaction.token?.ticker;
  const tokenFeeTip = tokenFeeFull ? `Token fee: ${tokenFeeFull}${tokenTicker ? ` ${tokenTicker}` : ''}` : null;
  const combinedFeesTip = [feesTip, tokenFeeTip].filter(Boolean).join(' · ');
  const feesDisplay = [currencyFee, tokenFeeShort].filter(Boolean).join(' / ');

  const dotted = 'underline decoration-dotted underline-offset-2';
  const locationName = (id) => tokenLocations.find((location) => location.id === id)?.name || id;
  const locationLabel = txType === TransactionType.INTERNAL_TRANSFER
    ? (transaction.sourceLocationId || transaction.destinationLocationId
        ? `${locationName(transaction.sourceLocationId) || 'Unknown'} → ${locationName(transaction.destinationLocationId) || 'Unknown'}`
        : '-')
    : (transaction.locationId ? locationName(transaction.locationId) : '-');

  const formatCost = () => {
    if (txType === TransactionType.BUY) {
      return transaction.cost ? (
        costTip ? (
          <Tooltip title={costTip} position="top" trigger="mouseenter" arrow={true} distance={8}>
            <span className={dotted}>{formatCurrency(transaction.cost, transaction.currency)}</span>
          </Tooltip>
        ) : (
          <span>{formatCurrency(transaction.cost, transaction.currency)}</span>
        )
      ) : '-';
    } else if (txType === TransactionType.SELL && transaction.costBasis) {
      // For sell transactions, show the cost basis in grey
      // Use portfolio currency for consistency (basis is scaled server-side)
      return <span className="text-gray-500">{formatCurrency(transaction.costBasis, currentPortfolio?.currency || 'USD')}</span>;
    }
    return '-';
  }

  const rowOpacity = transaction.hidden === true ? 'opacity-50' : '';
  return (
    <tr className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${rowOpacity} ${isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
      {onToggleSelect && (
        <td className="py-2 pl-2 pr-1 w-8">
          <input
            type="checkbox"
            checked={!!isSelected}
            onChange={(e) => { e.stopPropagation(); onToggleSelect(transaction.id); }}
            className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
          />
        </td>
      )}
      <td className="py-2 text-xs whitespace-nowrap">
        {formattedDate} <span className="ml-2 text-gray-800 dark:text-gray-400 text-[11px] font-medium">{formattedTime}</span>
      </td>
      <td className={`py-2 text-xs whitespace-nowrap font-medium ${txTypeColor}`}>
        <span className="inline-flex items-center gap-1.5">
          {txTypeDisplay}
          <FailedTransactionWarning transaction={transaction} />
        </span>
      </td>
      <td className="py-2 text-xs text-right whitespace-nowrap">
        {TransactionType.isTransfer(txType) || txType === TransactionType.SPLIT
          ? '-' 
          : priceTip ? (
              <Tooltip title={priceTip} position="top" trigger="mouseenter" arrow={true} distance={8}>
                <span className={dotted}>{formatPrice(transaction.price, transaction.currency)}</span>
              </Tooltip>
            ) : (
              <span>{formatPrice(transaction.price, transaction.currency)}</span>
            )}
      </td>
      <td className={`py-2 text-xs text-right whitespace-nowrap ${getQuantityColor()}`}>
        {formatQuantity()}
      </td>
      <td className="py-2 text-xs text-right whitespace-nowrap">
        {formatCost()}
      </td>
      <td className="py-2 text-xs text-right whitespace-nowrap">
        {(txType === TransactionType.SELL || txType === TransactionType.TRANSFER_OUT)
          ? proceedsTip ? (
              <Tooltip title={proceedsTip} position="top" trigger="mouseenter" arrow={true} distance={8}>
                <span className={dotted}>{formatCurrency(transaction.proceeds, transaction.currency)}</span>
              </Tooltip>
            ) : (
              <span>{formatCurrency(transaction.proceeds, transaction.currency)}</span>
            )
          : '-'}
      </td>
      <td className="py-2 text-xs text-right whitespace-nowrap">
        {feesDisplay ? (
          combinedFeesTip ? (
            <Tooltip title={combinedFeesTip} position="top" trigger="mouseenter" arrow={true} distance={8}>
              <span className={dotted}>{feesDisplay}</span>
            </Tooltip>
          ) : (
            <span>{feesDisplay}</span>
          )
        ) : '-'}
      </td>
      <td className="py-2 text-xs text-right whitespace-nowrap">
        <span className={getChangeColorClass(realizedPnL.toString())}>
          {realizedPnL}
        </span>
      </td>
      <td className="py-2 text-xs text-right whitespace-nowrap">
        {(() => {
          const content = unrealizedPnL !== null && !isNaN(unrealizedPnL)
            ? <span className={`${getChangeColorClass(unrealizedPnL.toString())} ${unrealized?.tip ? dotted : ''}`}>
                {formatCurrency(unrealizedPnL, currentPortfolio?.currency || 'USD')}
              </span>
            : unrealized?.sold
              ? <span className="text-[11px] text-gray-400 dark:text-gray-500">sold</span>
              : null;
          return content && unrealized?.tip ? (
            <Tooltip title={unrealized.tip} position="top" trigger="mouseenter" arrow={true} distance={8}>
              {content}
            </Tooltip>
          ) : content;
        })()}
      </td>
      <td className="py-2 text-xs text-right whitespace-nowrap max-w-[10rem] truncate" title={locationLabel}>{locationLabel}</td>
      <td className="py-2 text-xs text-right whitespace-nowrap max-w-xs truncate">{transaction.notes || '-'}</td>
      <td className="py-2 text-xs whitespace-nowrap">
        <div className="flex items-center justify-center space-x-3">
          <button
            onClick={(e) => { e.stopPropagation(); onToggleEnabled?.(transaction); }}
            className={`p-1 ${transaction.hidden === true ? 'text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400' : 'text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100'}`}
            aria-label={transaction.hidden === true ? 'Unhide transaction' : 'Hide transaction'}
            title={transaction.hidden === true ? 'Unhide transaction' : 'Hide transaction'}
          >
            {transaction.hidden === true ? (
              // Eye off (hidden)
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9.27-3.11-11-7  .6-1.43 1.5-2.72 2.6-3.79m3.05-2.18A10.05 10.05 0 0112 5c5 0 9.27 3.11 11 7-.53 1.25-1.29 2.39-2.22 3.36M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3l18 18" />
              </svg>
            ) : (
              // Eye (visible)
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
          <button 
            onClick={(e) => { e.stopPropagation(); onEdit(transaction); }}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 p-1"
            title="Edit transaction"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
          </button>
          {onMove && (
            <button
              onClick={(e) => { e.stopPropagation(); onMove(transaction); }}
              className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 p-1"
              title="Move to another token"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
            </button>
          )}
          <button 
            onClick={(e) => { e.stopPropagation(); onDelete(transaction); }}
            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </button>
        </div>
      </td>
    </tr>
  );
};


export const TransactionMobileCard = ({ transaction, token, currentPrice, tokenLocations = [], onEdit, onDelete, onToggleEnabled, onMove, isSelected, onToggleSelect, isExpanded, onToggle }) => {
  const { currentPortfolio } = usePortfolioContext();
  const txType = transaction.transactionType?.toUpperCase?.() || transaction.transactionType;
  const sv = transaction?.scaledValues;
  const portfolioCurrency = currentPortfolio?.currency || 'USD';
  const formattedDate = format(new Date(transaction.timestamp), 'dd MMM yyyy');
  const formattedTime = format(new Date(transaction.timestamp), 'HH:mm');
  const txTypeDisplay = TransactionType.getDisplayName(txType);
  const txTypeColor = TransactionType.getTypeColor(txType);
  const tokenTicker = token?.ticker || transaction.token?.ticker || 'TOKEN';
  const dotted = 'underline decoration-dotted underline-offset-2';
  const showTip = !!sv;
  const priceTip = showTip && sv?.price ? `In ${portfolioCurrency}: ${formatPrice(sv.price, portfolioCurrency)}` : null;
  const costTip = showTip && sv?.cost && txType === TransactionType.BUY ? `In ${portfolioCurrency}: ${formatCurrency(sv.cost, portfolioCurrency)}` : null;
  const proceedsTip = showTip && sv?.proceeds && (txType === TransactionType.SELL || txType === TransactionType.TRANSFER_OUT) ? `In ${portfolioCurrency}: ${formatCurrency(sv.proceeds, portfolioCurrency)}` : null;
  const feesTip = showTip && sv?.fees && transaction.fees !== '0' ? `In ${portfolioCurrency}: ${formatCurrency(sv.fees, portfolioCurrency)}` : null;
  const hasPositiveDecimal = (value) => parseFloatFromString(value) > 0;
  const currencyFee = hasPositiveDecimal(transaction.fees) ? formatCurrency(transaction.fees, transaction.currency) : null;
  const tokenFeeFull = hasPositiveDecimal(transaction.feeQuantity) ? String(transaction.feeQuantity) : null;
  const tokenFeeShort = tokenFeeFull ? formatAmount(tokenFeeFull, '') : null;
  const tokenFeeTip = tokenFeeFull ? `Token fee: ${tokenFeeFull} ${tokenTicker}` : null;
  const feesDisplay = [currencyFee, tokenFeeShort].filter(Boolean).join(' / ');
  const combinedFeesTip = [feesTip, tokenFeeTip].filter(Boolean).join(' · ');
  const stopCardToggle = (event) => event.stopPropagation();
  const mobileTooltipValue = (value) => (
    <span
      className={dotted}
      onClick={stopCardToggle}
      onKeyDown={stopCardToggle}
    >
      {value}
    </span>
  );
  const realizedPnL = (txType === TransactionType.SELL || txType === TransactionType.TRANSFER_OUT) && transaction.realizedPnl
    ? formatCurrency(transaction.realizedPnl, portfolioCurrency)
    : '-';

  const unrealized = computeUnrealizedPnL(transaction, txType, currentPrice);
  const unrealizedPnL = unrealized?.pnl ?? null;

  const locationName = (id) => tokenLocations.find((location) => location.id === id)?.name || id;
  const locationLabel = txType === TransactionType.INTERNAL_TRANSFER
    ? (transaction.sourceLocationId || transaction.destinationLocationId
        ? `${locationName(transaction.sourceLocationId) || 'Unknown'} → ${locationName(transaction.destinationLocationId) || 'Unknown'}`
        : '-')
    : (transaction.locationId ? locationName(transaction.locationId) : '-');

  const isOut = txType === TransactionType.SELL || txType === TransactionType.TRANSFER_OUT;
  const quantityColor = txType === TransactionType.SPLIT ? TransactionType.getTypeColor(txType) : isOut ? 'text-red-600 dark:text-red-500' : 'text-green-600 dark:text-green-500';
  const signedQuantity = `${txType === TransactionType.SPLIT ? '×' : isOut ? '-' : '+'}${transaction.quantity}`;
  const primaryValue = txType === TransactionType.BUY
    ? (transaction.cost ? formatCurrency(transaction.cost, transaction.currency) : '-')
    : (txType === TransactionType.SELL || txType === TransactionType.TRANSFER_OUT)
      ? (transaction.proceeds ? formatCurrency(transaction.proceeds, transaction.currency) : '-')
      : signedQuantity;

  const formatCost = () => {
    if (txType === TransactionType.BUY) {
      if (!transaction.cost) return '-';
      return costTip ? (
        <Tooltip title={costTip} position="top" trigger="mouseenter" arrow={true} distance={8}>
          {mobileTooltipValue(formatCurrency(transaction.cost, transaction.currency))}
        </Tooltip>
      ) : formatCurrency(transaction.cost, transaction.currency);
    }
    if (txType === TransactionType.SELL && transaction.costBasis) {
      return <span className="text-gray-500">{formatCurrency(transaction.costBasis, portfolioCurrency)}</span>;
    }
    return '-';
  };

  const priceDisplay = TransactionType.isTransfer(txType) || txType === TransactionType.SPLIT
    ? '-'
    : priceTip ? (
        <Tooltip title={priceTip} position="top" trigger="mouseenter" arrow={true} distance={8}>
          {mobileTooltipValue(formatPrice(transaction.price, transaction.currency))}
        </Tooltip>
      ) : formatPrice(transaction.price, transaction.currency);

  const proceedsDisplay = (txType === TransactionType.SELL || txType === TransactionType.TRANSFER_OUT)
    ? proceedsTip ? (
        <Tooltip title={proceedsTip} position="top" trigger="mouseenter" arrow={true} distance={8}>
          {mobileTooltipValue(formatCurrency(transaction.proceeds, transaction.currency))}
        </Tooltip>
      ) : formatCurrency(transaction.proceeds, transaction.currency)
    : '-';

  const feesNode = feesDisplay ? (
    combinedFeesTip ? (
      <Tooltip title={combinedFeesTip} position="top" trigger="mouseenter" arrow={true} distance={8}>
        {mobileTooltipValue(feesDisplay)}
      </Tooltip>
    ) : feesDisplay
  ) : '-';

  const rowOpacity = transaction.hidden === true ? 'opacity-50' : '';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle?.();
        }
      }}
      className={`rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 text-xs shadow-sm ${rowOpacity} ${isSelected ? 'ring-1 ring-blue-400 bg-blue-50 dark:bg-blue-900/20' : ''}`}
    >
      <div className="flex items-center gap-2">
        {onToggleSelect && (
          <input
            type="checkbox"
            checked={!!isSelected}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggleSelect(transaction.id)}
            className="h-3.5 w-3.5 shrink-0 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 cursor-pointer"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`font-semibold ${txTypeColor}`}>{txTypeDisplay}</span>
            <FailedTransactionWarning transaction={transaction} />
            <span className="truncate text-gray-500 dark:text-gray-400">{formattedDate}</span>
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-gray-700 dark:text-gray-200">
            <span className={`font-medium ${quantityColor}`}>{signedQuantity} {tokenTicker}</span>
            <span className="truncate text-gray-500 dark:text-gray-400">{locationLabel}</span>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-semibold text-gray-900 dark:text-gray-100">{primaryValue}</div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400">{isExpanded ? 'Hide details' : 'Details'}</div>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-3 border-t border-gray-200 dark:border-gray-700 pt-2">
          <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-gray-700 dark:text-gray-200">
            <div><span className="text-gray-500 dark:text-gray-400">Time</span><br />{formattedTime}</div>
            <div><span className="text-gray-500 dark:text-gray-400">Price</span><br />{priceDisplay}</div>
            <div><span className="text-gray-500 dark:text-gray-400">Cost</span><br />{formatCost()}</div>
            <div><span className="text-gray-500 dark:text-gray-400">Proceeds</span><br />{proceedsDisplay}</div>
            <div><span className="text-gray-500 dark:text-gray-400">Fees</span><br />{feesNode}</div>
            <div><span className="text-gray-500 dark:text-gray-400">Realized PnL</span><br /><span className={getChangeColorClass(realizedPnL.toString())}>{realizedPnL}</span></div>
            <div><span className="text-gray-500 dark:text-gray-400">Unrealized PnL</span><br />{unrealizedPnL !== null && !isNaN(unrealizedPnL) ? <span className={getChangeColorClass(unrealizedPnL.toString())}>{formatCurrency(unrealizedPnL, portfolioCurrency)}</span> : unrealized?.sold ? <span className="text-[11px] text-gray-400 dark:text-gray-500" title={unrealized.tip}>sold</span> : null}{unrealized?.tip && !unrealized.sold && <span className="block text-[11px] text-gray-500 dark:text-gray-400">{unrealized.tip}</span>}</div>
            <div><span className="text-gray-500 dark:text-gray-400">Status</span><br />{transaction.hidden === true ? 'Hidden' : 'Visible'}</div>
          </div>
          {transaction.notes && (
            <div className="mt-2 text-gray-700 dark:text-gray-200">
              <span className="text-gray-500 dark:text-gray-400">Notes</span><br />{transaction.notes}
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" onClick={(e) => { e.stopPropagation(); onToggleEnabled?.(transaction); }} className="rounded bg-gray-100 dark:bg-gray-700 px-2 py-1 text-xs text-gray-700 dark:text-gray-200">
              {transaction.hidden === true ? 'Unhide' : 'Hide'}
            </button>
            <button type="button" onClick={(e) => { e.stopPropagation(); onEdit(transaction); }} className="rounded bg-blue-100 dark:bg-blue-900/30 px-2 py-1 text-xs font-medium text-blue-800 dark:text-blue-300">
              Edit
            </button>
            {onMove && (
              <button type="button" onClick={(e) => { e.stopPropagation(); onMove(transaction); }} className="rounded bg-purple-100 dark:bg-purple-900/30 px-2 py-1 text-xs font-medium text-purple-800 dark:text-purple-300">
                Move
              </button>
            )}
            <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(transaction); }} className="rounded bg-red-100 dark:bg-red-900/30 px-2 py-1 text-xs font-medium text-red-800 dark:text-red-300">
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionRow; 